// ADR-040 CONTRACT Step 4b: Term-side resolution (kind-only).
//
// The structural Term side is identified purely by its machine identity
// `kind === 'term'`. The transitional `label === 'Term'` fallback (Step 1) is now
// RETIRED: the backend guarantees `kind` is NOT NULL + canonical (Step 2) and
// exactly one `kind='term'` side per card (Step 3), so every card resolves its
// Term by kind alone. The `label` column is untouched (cosmetic display name); CSV
// import/export still use the label as their wire format (out of scope). Every
// frontend Term-reader uses these helpers so the resolution is uniform.

/** Minimal shape any side object exposes for Term identity. */
type TermIdentity = { kind?: string | null }

/** The label `+ Field`'s Custom pill pre-fills nothing with — kept only as the
 * placeholder shape `fieldTypeLabel` needs when there is genuinely no side yet
 * to read a name from (see its own doc). NOT a sentinel for "no custom name"
 * on a stored side any more — see the amendment below `isCustomNamedSide`. */
export const PLAIN_NOTE_LABEL = 'Note'

/**
 * True when a side is a note-kind side — the Custom field type. ADR-040:
 * `kind` is machine identity and `label` is the cosmetic display name —
 * "display name = label if set, else the kind name". `note` is the one kind
 * whose label is authored rather than fixed, so it is the one kind where that
 * rule is observable: every note-kind side's label IS the name its author
 * typed, whatever word they chose.
 *
 * There used to be a second reading of `note`: a "plain Note" pill that made
 * an unnamed field, told apart from Custom by comparing the stored label
 * against the literal string "Note" — a label deciding a kind, which is
 * exactly the thing ADR-040 says `kind` alone should decide. `+ Field` has
 * offered no such pill since ADR-067 amendment 8 (Decision E): the chooser's
 * Custom flow is the ONLY path that creates a note-kind side, and it always
 * asks for a name first, so nothing genuinely unnamed can be created any
 * more. The old comparison survived past the pill it was written for and
 * turned into a trap: an author who typed "Note" as their OWN chosen name for
 * a Custom field — the reproduced case — was silently read as the extinct
 * plain type, shown under the translated kind name instead of the word they
 * typed, and locked against renaming. A stored label is text the author
 * wrote, not evidence about what kind of field it is; `kind` already carries
 * that, unambiguously, for every side including this one.
 *
 * The single predicate for both directions: the editor's draft round-trip
 * (`sideToDraft`) and every renderer's label resolution (`sideKindLabel`) call
 * this, so a field cannot be custom on one side of the trip and plain on the
 * other. A null kind is read as `note`, matching the backend's
 * `derive_kind_from_label` fallback.
 */
export function isCustomNamedSide(side: { kind?: string | null; label: string }): boolean {
  return (side.kind ?? 'note') === 'note'
}

/** The user-chosen name on a note-kind side, trimmed and returned verbatim —
 * whatever word it is, including one that happens to match another kind's
 * translated display name or the retired plain-Note pill's old label. See
 * `isCustomNamedSide` for why the label is never read as evidence of type. */
export function customSideName(side: { label: string }): string {
  return side.label.trim()
}

/** True when a side is the structural Term side (kind === 'term'). */
export function isTermSide(side: TermIdentity): boolean {
  return side.kind === 'term'
}

/**
 * Resolve the Term side from a list by `kind === 'term'`. Returns undefined only
 * when no side carries that kind; given the enforced exactly-one-term invariant a
 * well-formed card has exactly one, so this resolves the same Term the kind-first
 * + label-fallback form did, with the redundant label branch removed.
 */
export function findTermSide<T extends TermIdentity>(
  sides: readonly T[],
): T | undefined {
  return sides.find((s) => s.kind === 'term')
}
